import React from 'react';
import { Card } from '../../../components/shared';

const STATUS_STYLES = {
  PENDING: 'bg-yellow-100 text-yellow-700',
  OPEN: 'bg-blue-100 text-blue-700',
  PARTIALLY_FILLED: 'bg-blue-100 text-blue-700',
  FILLED: 'bg-green-100 text-green-700',
  CANCELLED: 'bg-gray-100 text-gray-500',
  REJECTED: 'bg-red-100 text-red-700',
};

const STATUS_LABELS = {
  PENDING: 'Chờ xử lý',
  OPEN: 'Đang mở',
  PARTIALLY_FILLED: 'Khớp một phần',
  FILLED: 'Đã khớp',
  CANCELLED: 'Đã hủy',
  REJECTED: 'Bị từ chối',
};

function formatVnd(value) {
  if (value == null) return '—';
  return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(value);
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function DetailRow({ label, value }) {
  return (
    <div className="flex justify-between border-b border-gray-50 py-2.5">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm font-medium text-gray-900">{value}</span>
    </div>
  );
}

export default function OrderDetail({ order, onClose, onApprove, onConfirmPayment, actionLoading }) {
  if (!order) return null;

  const isBuy = order.side === 'BUY';
  const canConfirmPayment = order.status === 'PENDING' && order.originalStatus === 'ORDER_INIT';
  const canApprove = order.status === 'OPEN' || order.status === 'PENDING';

  return (
    <Card>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-lg font-bold text-gray-900">{order.symbol || 'N/A'}</span>
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${isBuy ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              {isBuy ? 'MUA' : 'BÁN'}
            </span>
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_STYLES[order.status] || 'bg-gray-100 text-gray-500'}`}>
              {STATUS_LABELS[order.status] || order.status}
            </span>
          </div>
          {onClose && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        <div>
          <DetailRow label="Mã lệnh" value={order.brokerOrderId || order.id} />
          <DetailRow label="Loại lệnh" value={order.orderType || '—'} />
          <DetailRow label="Số lượng" value={order.quantity ?? '—'} />
          <DetailRow label="Giá" value={formatVnd(order.price)} />
          <DetailRow label="Tổng tiền" value={formatVnd(order.amount)} />
          {order.filledQuantity != null && (
            <DetailRow label="Đã khớp" value={order.filledQuantity} />
          )}
          {order.filledAvgPrice != null && (
            <DetailRow label="Giá khớp TB" value={formatVnd(order.filledAvgPrice)} />
          )}
          <DetailRow label="Ngày đặt" value={formatDate(order.submittedAt)} />
          {order.filledAt && (
            <DetailRow label="Ngày khớp" value={formatDate(order.filledAt)} />
          )}
          {order.originalStatus && (
            <DetailRow label="Trạng thái gốc" value={order.originalStatus} />
          )}
        </div>

        <div className="flex gap-2 pt-2">
          {canConfirmPayment && onConfirmPayment && (
            <button
              type="button"
              disabled={actionLoading}
              onClick={() => onConfirmPayment(order.brokerOrderId)}
              className="flex-1 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
            >
              {actionLoading ? 'Đang xử lý...' : 'Xác nhận thanh toán'}
            </button>
          )}

          {canApprove && onApprove && (
            <button
              type="button"
              disabled={actionLoading}
              onClick={() => onApprove(order.id)}
              className="flex-1 rounded-lg bg-green-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-green-700 disabled:opacity-50"
            >
              {actionLoading ? 'Đang xử lý...' : 'Duyệt lệnh'}
            </button>
          )}
        </div>
      </div>
    </Card>
  );
}
